/**
 * Pastille « nouvelle version » sur le bouton de version, en pied de barre
 * latérale.
 *
 * POURQUOI CES DIX LIGNES COMPTENT AUTANT QUE LE RESTE. `/changelog` n'a qu'UN
 * seul point d'entrée dans tout le produit : un `v1.0.0` en `text-[10px]
 * text-muted-foreground`, sans pastille et sans notification. Sans ce signal,
 * on cisèle une page de communication que personne n'ouvre.
 *
 * ⚠️ Le compte ne porte QUE sur ce que ce tenant peut réellement utiliser :
 * annoncer « 12 nouveautés » à quelqu'un qui n'en verra que quatre est une
 * promesse qu'on ne tient pas, sur le seul signal qu'il recevra.
 */

import { useMemo } from 'react'
import { useTenantConfig } from '@/hooks/useTenantConfig'
import { releasesAffichees as releases } from '@/data/releasesAffichees'
import { RUBRIQUES } from './releaseHelpers'

const CLE = 'argalis:changelog:derniere-version-lue'

/**
 * Lit la dernière version consultée.
 *
 * ⚠️ Toute lecture est gardée : en navigation privée, avec les données de site
 * bloquées, ou pendant une capture de vignette, l'ACCESSEUR LUI-MÊME lève. Un
 * `localStorage` indisponible doit rendre la page normale, pas la casser.
 */
function derniereVersionLue(): string | null {
    try {
        return localStorage.getItem(CLE)
    } catch {
        return null
    }
}

/** Mémorise la version consultée. Appelé par la page à son ouverture. */
export function marquerChangelogLu(version: string): void {
    try {
        localStorage.setItem(CLE, version)
    } catch {
        /* Stockage indisponible : la pastille réapparaîtra, c'est tout. */
    }
}

/**
 * Nombre d'éléments servis à ce tenant dans les versions non encore lues.
 * Rend 0 quand il n'y a rien à signaler — la pastille n'est alors pas rendue.
 */
export function useChangelogNonLu(): number {
    const { hasFeature, isLoading } = useTenantConfig()

    return useMemo(() => {
        // On ne décide rien tant que la configuration n'est pas arrivée :
        // `hasFeature` répond NON à tort sur le repli de chargement, et la
        // pastille annoncerait 0 puis sauterait à 5.
        if (isLoading || releases.length === 0) return 0

        const lue = derniereVersionLue()
        // Jamais ouvert : on ne signale QUE la version en cours. Annoncer
        // l'historique entier à quelqu'un qui vient de s'inscrire lui reproche
        // d'avoir raté ce qu'il ne pouvait pas voir.
        const rang = lue ? releases.findIndex((r) => r.version === lue) : 1
        const nonLues = releases.slice(0, rang < 0 ? 1 : rang)

        return nonLues.reduce(
            (total, r) =>
                total +
                RUBRIQUES.reduce(
                    (n, rub) =>
                        n + (r[rub] ?? []).filter((i) => !i.feature || hasFeature(i.feature)).length,
                    0,
                ),
            0,
        )
    }, [hasFeature, isLoading])
}
